import { createContext, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import { empresasService } from "app/services";
import { ErrorMessage, Spinner } from "app/components";

const EncargadosContext = createContext();

export function EncargadosProvider({ children }) {
  const params = useParams();
  const navigate = useNavigate();

  const encargadosQuery = useQuery({
    queryKey: ["empresa:get:encargados"],
    queryFn: () =>
      empresasService.getEncargados({ empresaId: params.empresa_id }),
  });

  const encargadoAddMutation = useMutation({
    queryKey: ["empresa:add:encargado", params.empresa_id],
    mutationFn: (data) =>
      toast.promise(empresasService.addEncargado(data), {
        loading: "Agregando el encargado...",
        success: (response) => response.statusText,
        error: (error) => error.response.statusText,
      }),
    onSuccess: async () => {
      navigate(".");

      return await encargadosQuery.refetch();
    },
  });

  const encargadoUpdateMutation = useMutation({
    queryKey: ["empresa:update:encargado"],
    mutationFn: (data) =>
      toast.promise(empresasService.updateEncargado(data), {
        loading: "Actualizando el encargado...",
        success: (response) => response.statusText,
        error: (error) => error.response.statusText,
      }),
    onSuccess: async () => {
      navigate(".");

      return await encargadosQuery.refetch();
    },
  });

  const encargadoDeleteMutation = useMutation({
    queryKey: ["empresa:delete:encargado"],
    mutationFn: (data) =>
      toast.promise(empresasService.deleteEncargado(data), {
        loading: "Eliminando el encargado...",
        success: (response) => response.statusText,
        error: (error) => error.response.statusText,
      }),
    onSuccess: async () => {
      navigate(".");

      return await encargadosQuery.refetch();
    },
  });

  const encargadoVerifyMutation = useMutation({
    mutationKey: ["empresa:verify:encargado"],
    mutationFn: (data) =>
      toast.promise(empresasService.verifyEncargado(data), {
        loading: "Verificando el encargado...",
        success: (response) => response.statusText,
        error: (error) => error.response.statusText,
      }),
    onSuccess: async () => {
      navigate(".");

      return await encargadosQuery.refetch();
    },
  });

  async function addEncargado({ encargado }) {
    const { empresa_id, encargado_id, ...rest } = encargado;

    return await encargadoAddMutation.mutateAsync({
      empresaId: params.empresa_id,
      encargado: rest,
    });
  }

  async function updateEncargado({ encargadoId, encargado }) {
    const { encargado_verificado, ...rest } = encargado;

    return await encargadoUpdateMutation.mutateAsync({
      empresaId: params.empresa_id,
      encargadoId,
      encargado: rest,
    });
  }

  async function deleteEncargado({ encargadoId }) {
    const deleted = window.confirm("¿Desea eliminar este encargado?");

    if (!!deleted) {
      await encargadoDeleteMutation.mutateAsync({
        empresaId: params.empresa_id,
        encargadoId: encargadoId,
      });

      return true;
    }

    return false;
  }

  async function verifyEncargado({ encargadoId }) {
    const confirmed = window.confirm("¿Desea verificar este encargado?");

    if (confirmed) {
      return await encargadoVerifyMutation.mutateAsync({
        empresaId: params.empresa_id,
        encargadoId,
      });
    }

    return false;
  }

  async function cancel() {
    if (window.confirm("¿Desea cancelar la edición?")) {
      return navigate(".");
    }
  }

  if (!!encargadosQuery.isLoading) {
    return <Spinner label="Cargando los encargados..." />;
  } else if (!!encargadosQuery.isError) {
    return <ErrorMessage message={encargadosQuery.error.response.statusText} />;
  }

  return (
    <EncargadosContext.Provider
      value={{
        encargados: encargadosQuery.data,
        isLoading: encargadosQuery.isLoading,
        isFetching: encargadosQuery.isFetching,
        isRefetching: encargadosQuery.isRefetching,
        addEncargado,
        isAdding: encargadoAddMutation.isLoading,
        updateEncargado,
        isUpdating: encargadoUpdateMutation.isLoading,
        deleteEncargado,
        isDeleting: encargadoDeleteMutation.isLoading,
        verifyEncargado,
        isVerifing: encargadoVerifyMutation.isLoading,
        cancel,
      }}
    >
      {children}
    </EncargadosContext.Provider>
  );
}

export function useEncargadosContext() {
  const encargadosContext = useContext(EncargadosContext);

  if (!encargadosContext) {
    throw new Error(
      "useEncargadosContext should be used inside the EncargadosProvider."
    );
  }

  return encargadosContext;
}
